// Browser-side download helper for the report artefacts.
//
// Wraps the existing core-engine Markdown / CSV / xlsx exporters and
// triggers a browser file download. No calculation logic is duplicated
// here — this is purely an I/O bridge.

import {
  formatNewSupplyResult,
  formatChangePremiumResult,
  formatExcessGfaResult,
  formatTemporaryUseResult,
  formatLeaseRentResult,
  formatCapacityIncreaseResult,
} from "../../export/markdown.js";
import {
  formatItemResultsCsv,
  formatChangePremiumCsv,
  formatBatchCsv,
} from "../../export/csv.js";
import { formatItemResultsXlsx } from "../../export/xlsx.js";
import type { CalculationResult } from "../../types/calculation-result.js";
import type { ChangePremiumResult } from "../../types/change-premium.js";
import type { ExcessGfaResult } from "../../logic/excess-gfa.js";
import type { TemporaryUseFeeResult } from "../../logic/temporary-use.js";
import type { KeyIndustrialLeaseAnnualRentResult } from "../../types/lease-rent.js";
import type { IndustrialCapacityIncreaseResult } from "../../types/capacity-increase.js";
import type { Parcel } from "../../types/parcel.js";

import type { RunResult } from "./runFile.js";

export type ExportFormat = "markdown" | "csv" | "xlsx";

export interface ExportPlan {
  readonly format: ExportFormat;
  readonly label: string;
}

export function availableExports(result: RunResult): ExportPlan[] {
  const out: ExportPlan[] = [{ format: "markdown", label: "下载 Markdown 报告" }];

  const isNewSupplyLike =
    result.calculationTypeRaw === "new-supply" ||
    result.calculationTypeRaw === "temporary-use" ||
    result.calculationTypeRaw === "short-term-lease" ||
    result.calculationTypeRaw === "lease-rent";

  const isChangeLike =
    result.calculationTypeRaw === "change-premium" ||
    result.calculationTypeRaw === "excess-gfa" ||
    result.calculationTypeRaw === "capacity-increase";

  if (isNewSupplyLike && result.itemResults) {
    out.push({ format: "csv", label: "下载 CSV（分项结果）" });
    if (result.calculationTypeRaw === "new-supply") {
      out.push({ format: "xlsx", label: "下载 xlsx（三表）" });
    }
  }
  if (isChangeLike) {
    out.push({ format: "csv", label: "下载 CSV（变更补缴）" });
  }
  if (result.calculationTypeRaw === "batch") {
    out.push({ format: "csv", label: "下载 CSV（批量汇总）" });
  }
  return out;
}

export async function downloadExport(
  result: RunResult,
  format: ExportFormat,
  filenameHint: string,
): Promise<void> {
  let blob: Blob;
  let filename: string;
  const base = sanitizeFilename(filenameHint);

  if (format === "markdown") {
    blob = new Blob([buildMarkdown(result)], {
      type: "text/markdown;charset=utf-8",
    });
    filename = `${base}.md`;
  } else if (format === "csv") {
    blob = new Blob([buildCsv(result)], { type: "text/csv;charset=utf-8" });
    filename = `${base}.csv`;
  } else {
    // xlsx
    const buf = await buildXlsx(result);
    blob = new Blob(
      [buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength)],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    );
    filename = `${base}.xlsx`;
  }

  triggerDownload(blob, filename);
}

// ---------------------------------------------------------------------------
// Markdown composer
// ---------------------------------------------------------------------------

function buildMarkdown(result: RunResult): string {
  switch (result.calculationTypeRaw) {
    case "new-supply":
      if (result.itemResults && result.marketPrices) {
        return formatNewSupplyResult(
          stubParcel(result),
          result.marketPrices,
          toCalculationResult(result),
        );
      }
      return "# 地价测算结果\n\n（缺少分项数据）\n";
    case "change-premium":
      if (result.beforeResult && result.afterResult) {
        const cp: ChangePremiumResult = {
          beforeResult: result.beforeResult,
          afterResult: result.afterResult,
          difference:
            result.afterResult.totalLandPrice -
            result.beforeResult.totalLandPrice,
          premium: result.premium ?? 0,
        };
        return formatChangePremiumResult(cp, result.projectName);
      }
      return "# 变更补缴测算结果\n\n（缺少数据）\n";
    case "excess-gfa":
      if (result.beforeResult && result.afterResult) {
        const eg: ExcessGfaResult = {
          base: {
            beforeResult: result.beforeResult,
            afterResult: result.afterResult,
            difference:
              result.afterResult.totalLandPrice -
              result.beforeResult.totalLandPrice,
            premium:
              (result.afterResult.totalLandPrice -
                result.beforeResult.totalLandPrice) *
              1.3,
          },
          excessGfaPremium: result.premium ?? 0,
          note: result.note,
        };
        return formatExcessGfaResult(eg);
      }
      return "# 竣工超面积测算结果\n\n（缺少数据）\n";
    case "temporary-use":
    case "short-term-lease":
      if (result.itemResults) {
        const tu: TemporaryUseFeeResult = {
          baseResult: toCalculationResult(result),
          totalFee: result.totalFee ?? 0,
          note: result.note,
        };
        return formatTemporaryUseResult(
          tu,
          result.projectName ?? "—",
          result.kindLabel ?? "临时用地使用费",
        );
      }
      return "# 临时用地 / 短期租赁\n\n（缺少数据）\n";
    case "lease-rent":
      if (result.itemResults) {
        const lr: KeyIndustrialLeaseAnnualRentResult = {
          baseResult: toCalculationResult(result),
          twentyYearLandPrice: result.twentyYearLandPrice ?? 0,
          annualRent: result.annualRent ?? 0,
          note: result.note,
          validationMessages: [...result.validationMessages],
        };
        return formatLeaseRentResult(lr);
      }
      return "# 重点产业项目年租金\n\n（缺少数据）\n";
    case "capacity-increase":
      if (result.beforeResult && result.afterResult) {
        const ci: IndustrialCapacityIncreaseResult = {
          base: {
            beforeResult: result.beforeResult,
            afterResult: result.afterResult,
            difference:
              result.afterResult.totalLandPrice -
              result.beforeResult.totalLandPrice,
            premium: result.premium ?? 0,
          },
          itemResults: [],
          capacityIncreasePremium: result.capacityIncreasePremium ?? 0,
        };
        return formatCapacityIncreaseResult(ci);
      }
      return "# 产业用地提高容积\n\n（缺少数据）\n";
    case "batch":
      return [
        "# 批量测算汇总",
        "",
        `> 项目：${result.projectName ?? "—"}`,
        "",
        "| 项目 | 值 |",
        "|---|---:|",
        `| 宗地地价合计 (元) | ${fmt(result.totalLandPrice ?? 0)} |`,
        `| 应补缴地价合计 (元) | ${fmt(result.premium ?? 0)} |`,
        `| 校验消息数量 | ${result.validationMessages.length} |`,
      ].join("\n");
    default:
      return "# 地价测算结果\n\n（未知类型）\n";
  }
}

// ---------------------------------------------------------------------------
// CSV composer
// ---------------------------------------------------------------------------

function buildCsv(result: RunResult): string {
  switch (result.calculationTypeRaw) {
    case "new-supply":
    case "temporary-use":
    case "short-term-lease":
    case "lease-rent": {
      if (!result.itemResults) return "\n";
      return formatItemResultsCsv(toCalculationResult(result));
    }
    case "change-premium":
    case "excess-gfa":
    case "capacity-increase": {
      if (result.beforeResult && result.afterResult) {
        const cp: ChangePremiumResult = {
          beforeResult: result.beforeResult,
          afterResult: result.afterResult,
          difference:
            result.afterResult.totalLandPrice -
            result.beforeResult.totalLandPrice,
          premium: result.premium ?? 0,
        };
        return formatChangePremiumCsv(cp);
      }
      return "\n";
    }
    case "batch":
      return formatBatchCsv([
        {
          index: 0,
          title: result.projectName,
          calculationType: "batch",
          result: null,
          validationMessages: [...result.validationMessages],
        },
      ]);
    default:
      return "\n";
  }
}

// ---------------------------------------------------------------------------
// xlsx composer (new-supply only)
// ---------------------------------------------------------------------------

async function buildXlsx(result: RunResult): Promise<Uint8Array> {
  if (!result.itemResults) {
    return new Uint8Array();
  }
  return formatItemResultsXlsx(
    toCalculationResult(result),
    result.marketPrices,
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toCalculationResult(result: RunResult): CalculationResult {
  return {
    itemResults: (result.itemResults ?? []) as CalculationResult["itemResults"],
    totalLandPrice: result.totalLandPrice ?? 0,
    totalChargeableGfa: result.totalChargeableGfa ?? 0,
    averageFloorLandPrice: result.averageFloorLandPrice ?? 0,
    validationMessages: [...result.validationMessages],
  };
}

function stubParcel(result: RunResult): Parcel {
  return {
    projectName: result.projectName ?? "—",
    parcelId: undefined,
    landArea: 0,
    supplyMethod: "招拍挂",
    calculationType: "新供应土地",
    calculationDate: result.calculationDate ?? "—",
    mainLandUse: "住宅",
    projectType: "普通项目",
    totalGfa: result.totalChargeableGfa ?? 0,
    governmentOwnedGfa: undefined,
    mainIndustrialLandType: undefined,
    remark: undefined,
  } as Parcel;
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/\.[^.]+$/, "")
    .replace(/[^A-Za-z0-9_\-一-龥]/g, "_")
    .slice(0, 80) || "report";
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return String(Math.round(n * 1_000_000) / 1_000_000);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
